import _ from 'lodash';
import type {
  WorkbookApprovalFlowAttachment,
  WorkbookApprovalFlowAttachmentData,
  WorkbookApprovalFlowData,
  WorkbookQueryWorkbook,
} from '@/worksheet/worksheet.types';
import type { GqlApprovalFlowSectionFragment } from '@seeqdev/api/graphql/ApprovalFlowSection';

function normalizeGqlApprovalFlowStep(step: GqlApprovalFlowSectionFragment['approvalFlows'][number]['steps'][number]) {
  return {
    id: step.id,
    approvalFlowId: step.approvalFlowId,
    name: step.name,
    lockRequired: step.lockRequired,
    position: step.position,
    archived: step.archived,
    createdAt: step.createdAt,
    updatedAt: step.updatedAt ?? undefined,
    permissions: step?.permissions,
    status: step.status,
    approval: step.approval
      ? {
          approvedAt: step.approval.approvedAt,
          // The server always names an approver, redacting ones the reader cannot see or that no longer exist.
          approver: { name: step.approval.approver.name as string, email: step.approval.approver.email },
        }
      : undefined,
  };
}

function normalizeGqlApprovalFlow(flow: GqlApprovalFlowSectionFragment['approvalFlows'][number]) {
  return {
    id: flow.id,
    name: flow.name,
    archived: flow.archived,
    createdAt: flow.createdAt,
    updatedAt: flow.updatedAt ?? undefined,
    steps: _.sortBy(flow.steps, 'position').map(normalizeGqlApprovalFlowStep),
  };
}

function normalizeGqlApprovalFlowAssignment(
  assignment: GqlApprovalFlowSectionFragment['approvalFlowAssignments'][number],
) {
  return {
    id: assignment.id,
    approvalFlowId: assignment.approvalFlowId,
    stepId: assignment.stepId,
    itemId: assignment.itemId,
    approvedBy: assignment.approvedBy,
    approvedAt: assignment.approvedAt,
  };
}

export function normalizeGqlApprovalFlowData(workbook: WorkbookQueryWorkbook | undefined): WorkbookApprovalFlowData {
  return {
    approvalFlows: (workbook?.approvalFlows || [])?.map(normalizeGqlApprovalFlow),
    approvalFlowAssignments: (workbook?.approvalFlowAssignments || [])?.map(normalizeGqlApprovalFlowAssignment),
    attachments: (workbook?.attachments || [])?.map(normalizeGqlAttachment),
  };
}

export function normalizeGqlAttachmentData(
  workbook: WorkbookQueryWorkbook | undefined,
): WorkbookApprovalFlowAttachmentData {
  return {
    attachments: (workbook?.attachments || [])?.map(normalizeGqlAttachment),
  };
}

export function normalizeGqlAttachment(
  attachment: GqlApprovalFlowSectionFragment['attachments'][number],
): WorkbookApprovalFlowAttachment {
  return {
    id: attachment.id,
    itemId: attachment.itemId,
    approvalFlowId: attachment.approvalFlowId,
    assignedBy: attachment.assignedBy,
    createdAt: attachment.createdAt,
    archived: attachment.archived,
  };
}
